<template>
  <section class="projects-section container py-5" id="proyectos">
    <div class="projects-head text-center mb-4">
      <span class="projects-chip">Proyectos reales</span>
      <h2 class="fw-bold mt-3 heading-glow">Sitios que ya estan en linea</h2>
      <p class="projects-subtitle mb-0">
        Explora una muestra de proyectos publicados y abre cada sitio con un clic.
      </p>
    </div>

    <div class="carousel-wrap">
      <button class="nav-btn prev" type="button" @click="prevSlide" aria-label="Proyecto anterior">
        <i class="bi bi-chevron-left"></i>
      </button>

      <div class="carousel-viewport">
        <div class="carousel-track" :style="trackStyle">
          <article v-for="project in projects" :key="project.url" class="project-card">
            <a :href="project.url" target="_blank" rel="noopener" class="project-thumb-link" :aria-label="`Abrir ${project.name}`">
              <img
                :src="project.thumb"
                :alt="`Vista previa de ${project.name}`"
                class="project-thumb"
                loading="lazy"
                @error="onImageError"
              />
            </a>

            <div class="project-body text-start">
              <h3 class="project-name mb-1">{{ project.name }}</h3>
              <p class="project-domain mb-3">{{ project.domain }}</p>
              <a :href="project.url" target="_blank" rel="noopener" class="project-link">
                Visitar sitio
              </a>
            </div>
          </article>
        </div>
      </div>

      <button class="nav-btn next" type="button" @click="nextSlide" aria-label="Proyecto siguiente">
        <i class="bi bi-chevron-right"></i>
      </button>
    </div>

    <div class="dots mt-4" role="tablist" aria-label="Navegacion del carrusel">
      <button
        v-for="dot in pageCount"
        :key="dot"
        class="dot"
        :class="{ active: currentIndex === dot - 1 }"
        type="button"
        :aria-label="`Ir a posicion ${dot}`"
        @click="goTo(dot - 1)"
      />
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

const projects = [
  {
    name: 'Color Mexique',
    domain: 'colormexique.mitikuin.com',
    url: 'https://colormexique.mitikuin.com/',
    thumb: 'https://image.thum.io/get/width/800/noanimate/https://colormexique.mitikuin.com/'
  },
  {
    name: 'Divinorum',
    domain: 'divinorum.com.mx',
    url: 'https://divinorum.com.mx/',
    thumb: 'https://image.thum.io/get/width/800/noanimate/https://divinorum.com.mx/'
  },
  {
    name: 'Ingles Davidovich Xalapa',
    domain: 'inglesdavidovichxalapa.mitikuin.com',
    url: 'https://inglesdavidovichxalapa.mitikuin.com/',
    thumb: 'https://image.thum.io/get/width/800/noanimate/https://inglesdavidovichxalapa.mitikuin.com/'
  },
  {
    name: 'Okamura Kai',
    domain: 'okamura-kai.mitikuin.com',
    url: 'https://okamura-kai.mitikuin.com/',
    thumb: 'https://image.thum.io/get/width/800/noanimate/https://okamura-kai.mitikuin.com/'
  },
  {
    name: 'Braseros Xalapa',
    domain: 'braserosxalapa.com',
    url: 'https://braserosxalapa.com/',
    thumb: 'https://image.thum.io/get/width/800/noanimate/https://braserosxalapa.com/'
  }
]

const currentIndex = ref(0)
const viewportWidth = ref(1280)
let autoPlayTimer = null

const cardsPerView = computed(() => {
  if (viewportWidth.value <= 576) return 1
  if (viewportWidth.value <= 992) return 2
  return 3
})

const maxIndex = computed(() => Math.max(0, projects.length - cardsPerView.value))
const pageCount = computed(() => maxIndex.value + 1)

const trackStyle = computed(() => {
  const width = (projects.length / cardsPerView.value) * 100
  const step = 100 / projects.length
  return {
    width: `${width}%`,
    transform: `translateX(-${currentIndex.value * step}%)`
  }
})

const syncViewport = () => {
  viewportWidth.value = window.innerWidth
}

const nextSlide = () => {
  currentIndex.value = currentIndex.value >= maxIndex.value ? 0 : currentIndex.value + 1
}

const prevSlide = () => {
  currentIndex.value = currentIndex.value <= 0 ? maxIndex.value : currentIndex.value - 1
}

const goTo = (index) => {
  currentIndex.value = Math.min(Math.max(index, 0), maxIndex.value)
}

const startAutoPlay = () => {
  stopAutoPlay()
  autoPlayTimer = setInterval(() => {
    nextSlide()
  }, 5200)
}

const stopAutoPlay = () => {
  if (autoPlayTimer) {
    clearInterval(autoPlayTimer)
    autoPlayTimer = null
  }
}

const onImageError = (event) => {
  event.target.src = '/assets/img/logo_mitikuinRedondeado.webp'
}

watch(cardsPerView, () => {
  if (currentIndex.value > maxIndex.value) {
    currentIndex.value = maxIndex.value
  }
})

onMounted(() => {
  syncViewport()
  window.addEventListener('resize', syncViewport)
  startAutoPlay()
})

onUnmounted(() => {
  window.removeEventListener('resize', syncViewport)
  stopAutoPlay()
})
</script>

<style scoped>
.projects-section {
  position: relative;
}

.projects-chip {
  display: inline-block;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.24);
  background: rgba(255, 255, 255, 0.08);
  color: #d9fcff;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0.35rem 0.75rem;
}

.projects-subtitle {
  color: #d4edff;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
}

.carousel-wrap {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.carousel-viewport {
  overflow: hidden;
  width: 100%;
}

.carousel-track {
  display: flex;
  transition: transform 0.5s ease;
}

.project-card {
  width: calc(100% / 5);
  padding: 0.65rem;
}

.project-card > .project-thumb-link,
.project-card > .project-body {
  border-radius: 1rem;
}

.project-thumb-link {
  display: block;
  border: 1px solid rgba(255, 255, 255, 0.16);
  overflow: hidden;
  box-shadow: 0 14px 26px rgba(0, 0, 0, 0.28);
}

.project-thumb {
  width: 100%;
  height: 190px;
  object-fit: cover;
  display: block;
  transition: transform 0.35s ease;
}

.project-card:hover .project-thumb {
  transform: scale(1.03);
}

.project-body {
  margin-top: 0.75rem;
  padding: 0.95rem;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.09), rgba(255, 255, 255, 0.02));
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.project-name {
  color: #f4fcff;
  font-size: 1.02rem;
  font-weight: 700;
}

.project-domain {
  color: #9fd2ea;
  font-size: 0.86rem;
}

.project-link {
  display: inline-block;
  text-decoration: none;
  color: #0a1d29;
  font-weight: 700;
  font-size: 0.88rem;
  border-radius: 999px;
  padding: 0.4rem 0.8rem;
  background: linear-gradient(90deg, #a8ff78, #78ffd6);
}

.nav-btn {
  width: 40px;
  height: 40px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  color: #e8f8ff;
  background: rgba(255, 255, 255, 0.07);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, border-color 0.2s ease;
}

.nav-btn:hover {
  transform: translateY(-1px);
  border-color: rgba(0, 229, 255, 0.5);
}

.dots {
  display: flex;
  justify-content: center;
  gap: 0.42rem;
}

.dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  border: 0;
  background: rgba(255, 255, 255, 0.28);
}

.dot.active {
  background: #78ffd6;
  box-shadow: 0 0 12px rgba(120, 255, 214, 0.7);
}

@media (max-width: 992px) {
  .project-thumb {
    height: 170px;
  }
}

@media (max-width: 576px) {
  .carousel-wrap {
    gap: 0.35rem;
  }

  .project-card {
    padding: 0.35rem;
  }

  .project-thumb {
    height: 180px;
  }

  .project-body {
    padding: 0.8rem;
  }

  .nav-btn {
    width: 34px;
    height: 34px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .carousel-track,
  .project-thumb,
  .nav-btn {
    transition: none;
  }
}
</style>
